<script setup lang="ts">
import type { InclusionOption } from '~/types/download'
import type { AcademicArticleSection } from '~/utils/solr/academic-article'
import { SolrParamsKey } from '#shared/solr'
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons'
import { useDownloadAccess } from '~/composables/download/useDownloadAccess'
import { useDownloadApi } from '~/composables/download/useDownloadApi'
import { mapAcademicArticleTocToDownloadNodes } from '~/utils/download/mapAcademicArticleTocToNodes'
import { trackableLegalAreas } from '~/utils/pendo/legal-areas'
import { createCheckedItemFromNode } from '~/utils/download/treeSelection'
import { RedirectEntryType } from '~~/shared/redirect-service-types'

const route = useRoute<'academic-article-by-id'>()
const articleId = computed(() => route.params.artikkelId?.[0] ?? '')

const { activeView, open, back, reset } = useReadingView('academic-article-view-stack')
const { sidebarExpanded } = useReadingSidebarDefault()

reset()
onScopeDispose(() => {
  reset()
})

// Tablet: Reference view opens beside the article text, so collapse the sidebar to a rail and restore it on return
const sidebarExpandedBeforeReference = ref<boolean>(true)
watch(activeView, (view, prev) => {
  if (!isTabletViewport()) {
    return
  }
  if (view === 'reference') {
    if (prev !== 'reference') {
      sidebarExpandedBeforeReference.value = sidebarExpanded.value
    }
    sidebarExpanded.value = false
  }
  else {
    sidebarExpanded.value = sidebarExpandedBeforeReference.value
  }
})

// If the user expands the rail while reference view is open, leaves view. Keep the user's new sidebar state
watch(sidebarExpanded, (expanded) => {
  if (!isTabletViewport()) {
    return
  }
  if (expanded && activeView.value === 'reference') {
    sidebarExpandedBeforeReference.value = true
    reset()
  }
})

// Reference view is temporary. Close it when the viewport changes so old state does not get stuck.
const isTablet = useMediaQuery(`(min-width: ${MOBILE_MAX_WIDTH + 1}px) and (max-width: ${TABLET_MAX_WIDTH}px)`)
watch(isTablet, () => {
  if (activeView.value === 'reference') {
    sidebarExpanded.value = sidebarExpandedBeforeReference.value
    reset()
  }
})

const hasLicense = useHasContentLicense()
const { isDark: readingDarkMode, toggle: toggleReadingDarkMode } = useReadingMode()
const { canDownload } = useDownloadAccess()
const { downloadFile } = useDownloadApi()
const toast = getToast()

// Fetch academic article root document by remoteId (canonical)
const { data: articleData, suspense: articleSuspense } = useSolrQueryV2(
  { [SolrParamsKey.ContentType]: ['academic_article'], [SolrParamsKey.RemoteId]: articleId.value },
  'book',
  'literature',
)

const article = computed(() => articleData.value?.response?.docs[0] ?? null)
await articleSuspense()

if (!article.value) {
  if (articleId.value) {
    const redirect = await redirectLookup(articleId.value, RedirectEntryType.ACADEMIC_ARTICLE);
    if (redirect) {
      navigateTo({
        name: 'academic-article-by-id',
        params: {
          artikkelId: [redirect.target],
        }
      })
    } else {
      throw createError({
        statusCode: 404,
        statusMessage: 'Fagartikkel ikke funnet',
        fatal: true,
      })
    }
  } else {
    // You should probably never get to this error
    // since [...artikkelId] has to be defined to hit this route
    // but as a failsafe throw not found error
    throw createError({
      statusCode: 404,
      statusMessage: 'Fagartikkel ikke funnet',
      fatal: true,
    })
  }
}

const title = computed(() => article.value?.title ?? '')
const mainNodeId = computed(() => (article.value?.ezMainNodeId ? String(article.value.ezMainNodeId) : ''))

// If the last reference card is closed, return to the text. `back()` restores focus to the tapped link.
const { lawReferenceNodeIds } = useArticleReferences(mainNodeId)
watch(lawReferenceNodeIds, (ids) => {
  if (!ids.length && activeView.value === 'reference' && !isDesktopViewport()) {
    back()
  }
})

// Sections for TOC (by main node ID from fetched article)
const sectionsQuery = computed(() => ({
  [SolrParamsKey.ContentType]: ['academic_article_section'],
  [SolrParamsKey.PathLocation]: mainNodeId.value || undefined,
  [SolrParamsKey.Sort]: 'priority asc',
}))

const { data: sectionsData, suspense: sectionsSuspense } = useSolrQueryV2(sectionsQuery, 'academicArticleSection', 'literature')
await sectionsSuspense()

const sections = computed<AcademicArticleSection[]>(() => (sectionsData.value?.response?.docs ?? []) as AcademicArticleSection[])
const selectedSectionId = ref<string | undefined>()
const isSingleSectionDownloadInProgress = ref(false)

function handleVisibleSectionChanged(sectionId: string | undefined) {
  if (!sectionId) {
    return
  }

  selectedSectionId.value = sectionId
}

function handleReferenceOpen(trigger: HTMLElement) {
  if (!isDesktopViewport()) {
    open('reference', trigger)
  }
}

function handleTocSectionSelected() {
  back()
}

const disableDownloadLabel = computed(() => {
  if (!canDownload.value) {
    return 'Nedlasting ikke tilgjengelig for dette innholdet'
  }

  return undefined
})

const inclusionOptions: InclusionOption[] = [
  { key: 'frontPage', label: 'Forside' },
  { key: 'tableOfContents', label: 'Innholdsfortegnelse' },
]

const articleTocForDownload = computed(() => mapAcademicArticleTocToDownloadNodes({
  sections: sections.value,
  article: article.value,
}))

const downloadModal = useTemplateRef('downloadModal')

async function openDownload() {
  const tocNodes = articleTocForDownload.value
  if (!tocNodes.length) {
    return
  }

  const selectedNode = tocNodes.find(node => node.id === selectedSectionId.value) ?? tocNodes[0]
  if (!selectedNode) {
    return
  }

  // Downloads directly if there's only one section in the article,
  // otherwise opens the download modal to let user select sections and options.
  if (tocNodes.length === 1) {
    if (isSingleSectionDownloadInProgress.value) {
      return
    }

    const checkedItem = createCheckedItemFromNode(selectedNode)
    if (!checkedItem) {
      return
    }

    isSingleSectionDownloadInProgress.value = true
    toast.info('Nedlasting startet')

    try {
      await downloadFile({
        items: [checkedItem],
        format: 'pdf',
        options: {
          lovtekst: false,
          curatedReferences: false,
          frontPage: true,
        },
        toc: [selectedNode],
      })
      toast.success('Nedlasting fullført')
    }
    catch (error) {
      console.error('Academic article download failed', error)
      toast.error('Noe gikk galt ved nedlasting. Prøv igjen.')
    }
    finally {
      isSingleSectionDownloadInProgress.value = false
    }

    return
  }

  downloadModal.value?.show?.({
    id: selectedNode.id,
  })
}

const breadcrumbOverrides = computed(() => {
  const segments = route.path.split('/')
  return segments.map((_, i) => {
    if (i < 2) {
      return undefined
    }
    if (i === 2) {
      return false
    }
    if (i === 3) {
      return { label: title.value, to: `/faglitteratur/artikkel/${articleId.value}` }
    }
    return false
  })
})

const contentDiv = useTemplateRef<ComponentPublicInstance | null>('contentDiv')
const { contentTop, bannerHeight, contentBottom, headerHeight, updateMeasurements } = useContentMeasurements(contentDiv)

watchEffect(() => {
  if (!import.meta.client) {
    return
  }

  document.body.style.cursor = isSingleSectionDownloadInProgress.value ? 'progress' : ''
})

onBeforeUnmount(() => {
  if (!import.meta.client) {
    return
  }

  document.body.style.cursor = ''
})

onMounted(() => updateMeasurements())

useTrackLastActivity(title)
usePendoContentTracking({
  type: 'academic_article',
  title,
  id: articleId,
  legalAreas: computed(() => trackableLegalAreas(!!article.value, article.value?.legal_areas)),
})

definePageMeta({
  name: 'academic-article-by-id',
  key: route => `article-${(route.params as { artikkelId?: string[] }).artikkelId?.[0] ?? ''}`,
  layout: 'default-with-mobile-notice',
  theme: 'carbon',
  mobileReady: true,
})

useHead({
  bodyAttrs: { class: 'bg-surface-primary reading-dark-scope' },
  title,
})
</script>

<template>
  <div>
    <ContentDiv ref="contentDiv" class="flex items-start">
      <div class="md:hidden flex items-center gap-2xs">
        <ControlButton
          size="small"
          :icon="faArrowLeft"
          aria-label="Tilbake"
          class="shrink-0 bg-transparent hover:bg-transparent dark:hover:bg-surface-secondary !p-2xs border rounded-lg"
          @click="navigateTo('/faglitteratur')"
        />
        <span class="text-xsmall" aria-hidden="true">Tilbake</span>
      </div>
      <Breadcrumbs :overrides="breadcrumbOverrides" hide-mobile-back />
      <ReadingActions
        v-if="hasLicense"
        :content="article"
        :disable-download-label="disableDownloadLabel"
        enable-dark-mode
        :dark-mode="readingDarkMode"
        @open-download="openDownload"
        @update:dark-mode="toggleReadingDarkMode"
      />
    </ContentDiv>

    <ReadingLayout
      v-model:sidebar-expanded="sidebarExpanded"
      fixed-content
      :content-top="contentTop"
      :content-bottom="contentBottom"
      :header-height="headerHeight"
      :banner-height="bannerHeight"
      :licensed-content="hasLicense"
      aside-width="narrow"
      context="academic_article"
      :active-view="activeView"
    >
      <template #sidebar>
        <AcademicArticleMobileTableOfContentBar mode="close" />
        <TableOfContentToggle
          title="Innhold"
          :show-search-option="false"
          class="sticky top-0 z-[9] bg-surface-elevated mb-1"
        />
        <TableOfContentItems v-model="selectedSectionId" :sections="sections" @select="handleTocSectionSelected" />
      </template>

      <template #sidebar-collapsed>
        <span class="text-small text-carbon-500 dark:text-carbon-50 select-none font-bold">Innholdsfortegnelse</span>
      </template>

      <template v-if="hasLicense" #content="{ isScrollTop }">
        <AcademicArticleMobileTableOfContentBar mode="open" />
        <LiteratureAcademicArticleContent
          :article-id="mainNodeId"
          :article="article"
          :sections="sections"
          @visible-section-changed="handleVisibleSectionChanged"
          @reference-open="handleReferenceOpen"
        >
          <template #header>
            <ReadingHeader v-if="article" :content="article" :is-scroll-top="isScrollTop" class="pt-xs" />
          </template>
        </LiteratureAcademicArticleContent>
      </template>
      <template v-else #content>
        <AcademicArticleMobileTableOfContentBar mode="open" />
        <ReadingNotAuthorized />
      </template>
      <template v-if="hasLicense" #aside>
        <LiteratureArticleReference :article-id="mainNodeId" />
      </template>
    </ReadingLayout>

    <DownloadModal
      v-if="hasLicense"
      ref="downloadModal"
      :toc="articleTocForDownload"
      :selected-id="selectedSectionId"
      :inclusion-options="inclusionOptions"
      :show-inclusion-options="true"
      render-leaf-nodes
      no-availability-label="Ingen nedlasting tilgjengelig"
    />
  </div>
</template>
